import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { store } from "../store";
import { performLogout, refreshToken } from "../store/features/userSlice";
import i18n from "../translations/i18n";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track if a token refresh is in progress to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue of failed requests that will be retried after token refresh
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      // Update the request config with the new token and retry
      if (prom.config.headers && token) {
        prom.config.headers.Authorization = `Bearer ${token}`;
      }
      prom.resolve(api(prom.config));
    }
  }

  failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = store.getState().user.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Set Accept-Language header based on selected language
    const language = i18n.language || localStorage.getItem("language") || "id";
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if this is a 401 error
    if (error.response?.status === 401 && originalRequest) {
      // Don't try to refresh or logout on login failures
      if (originalRequest.url?.includes("/api/v1/users/login")) {
        return Promise.reject(error);
      }
      // If this is the refresh endpoint itself, logout immediately
      if (originalRequest.url?.includes("/api/v1/users/refresh")) {
        store.dispatch(performLogout());
        return Promise.reject(error);
      }

      // If we've already retried this request, logout
      if (originalRequest._retry) {
        store.dispatch(performLogout());
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      // Mark that we're refreshing and retrying
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        await store.dispatch(refreshToken()).unwrap();
        const newToken = store.getState().user.token;

        // Process queued requests with the new token
        processQueue(null, newToken || null);

        // Retry the original request with the new token
        if (originalRequest.headers && newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout and reject all queued requests
        processQueue(refreshError as AxiosError);
        store.dispatch(performLogout());
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Return the original error to preserve the error structure
    return Promise.reject(error);
  },
);

export default api;
